import { isObject } from "lodash-es";

export type ToastType = "success" | "error" | "warning" | "info";

const TOAST_TYPE_ICONS: Record<ToastType, string> = {
  success: "mdi-check-circle",
  error: "mdi-alert",
  warning: "mdi-alert",
  info: "mdi-information",
};

export interface ToastMessage {
  text: string;
  color: ToastType;
  prependIcon?: string;
}

export interface ToastConfirmState {
  message: string;
  buttonText: string;
  resolve: (value: boolean) => void;
}

// Module-level state (singleton, shared across all callers)
const toastQueue = ref<ToastMessage[]>([]);
const confirmState = ref<ToastConfirmState | null>(null);

function push(message: { text: string; type: ToastType; }) {
  toastQueue.value = [...toastQueue.value, { 
    ...message, 
    color: message.type,
    prependIcon: TOAST_TYPE_ICONS[message.type],
  }];
}

function showConfirm(options: { message: string; buttonText: string }): Promise<boolean> {
  return new Promise((resolve) => {
    confirmState.value = {
      message: options.message,
      buttonText: options.buttonText,
      resolve: (value: boolean) => {
        confirmState.value = null;
        resolve(value);
      },
    };
  });
}

export function useToast() {
  return {
    toastQueue: toastQueue,
    confirmState: confirmState,
    push,
    showConfirm,
  };
}

// Helper functions (use module-level state so they work from anywhere)

export function formatErrorMessage({ error, message }: { error: any, message?: string}) {
  if (!message) {
    if (error.reason) {
      message = error.reason as string;
    } else if (error?.options?.method === 'GET') {
      message = 'Failed to load data';
    } else if (['POST', 'PUT', 'PATCH'].includes(error?.options?.method)) {
      message = 'Failed to save data';
    } else if (error?.options?.method === 'DELETE') {
      message = 'Failed to delete data';
    } else {
      message = 'Request error';
    }
  }
  if (error?.data?.detail) {
    message += ': ' + error?.data?.detail;
  } else if (Array.isArray(error?.data)) {
    message += ': ' + error.data.join(', ')
  } else if (error?.data?.non_field_errors) {
    message += ': ' + error.data.non_field_errors.join(', ');
  } else if (error?.status === 429) {
    message += ': Exceeded rate limit. Try again later.';
  } else if (isObject(error?.data)) {
    const fieldMessages = Object.entries(error.data)
      .filter(([_, v]) => Array.isArray(v) || typeof v === 'string')
      .flatMap(([key, v]) => {
        if (Array.isArray(v)) {
          return v.map((msg) => `${msg} ("${key}").`);
        }
        return [`${v} ("${key}").`];
      });
    message += ': ' + fieldMessages.join(', ');
  }

  return message;
}

export function requestErrorToast({ error, message }: { error: any; message?: string }) {
  // eslint-disable-next-line no-console
  console.log('Request error', { error, message }, error?.data);

  if (error?.options?.signal?.aborted) {
    return;  // Do not show toast for aborted requests
  }
  errorToast(formatErrorMessage({ error, message }));
}

export function successToast(message: string) {
  push({ text: message, type: "success" });
}

export function errorToast(message: string) {
  push({ text: message, type: "error" });
}

export function warningToast(message: string) {
  push({ text: message, type: "warning" });
}

export function infoToast(message: string) {
  push({ text: message, type: "info" });
}

export async function requestErrorToastWrapper(fn: () => any) {
  try {
    return await Promise.resolve(fn());
  } catch (error) {
    requestErrorToast({ error });
  }
}
